import React, { Component } from 'react'
import { connect } from 'react-redux'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import TextField from '@material-ui/core/TextField'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemText from '@material-ui/core/ListItemText'
import { filesStore, explorer, dcmIsOpen } from '../actions'
import Typography from '@material-ui/core/Typography'
import cornerstone from 'cornerstone-core'
import cornerstoneWADOImageLoader from 'cornerstone-wado-image-loader'
import dicomParser from 'dicom-parser'
import { groupBy } from '../functions'
import store from '../store'
import { getPacsConfig, buildOrthancUrl, isR2DirectEnabled, buildR2ManifestUrl, getAvailablePacs } from '../config/pacsConfig'
import { configureWadoLoaderAuth } from '../utils/wadoAuth'
import { loadStudyFromManifestObject } from '../utils/loadStudyFromManifest'
import {
  loadDicomInstanceWithCache,
  loadDicomInstanceDirectUrl,
} from '../utils/loadDicomInstanceWithCache'
import { listCompleteSopsForStudy } from '../db/dicomCache'
import { getWriteQueueStats } from '../db/dicomCacheWriteQueue'
import throttle from 'lodash/throttle'
import { stampIsScoutOnFileItem } from '../utils/isScoutSeries'
import { getManifestDownloadConcurrency } from '../utils/mobileDownloadProfile'
import {
  buildSeriesSlots,
  createSeriesPriorityScheduler,
  createDownloadProgressTracker,
  estimateLoadedBytes,
} from '../utils/downloadSeriesScheduler'

// Inicialización inmediata del estado del explorer en Redux
// Esto es crucial porque el componente Explorer accede a this.props.explorer.patient en su constructor
store.dispatch({
  type: 'EXPLORER_STORE',
  explorer: {
    folder: null,
    patient: {
      list: new Map(),
      keys: []
    }
  }
});

/** Aplica pacsConfig + arma state parcial (URL/user/pass). Source: ./config/pacsConfig.js */
function resolvePacsState(pacsIdFromUrl) {
  const pacsId = pacsIdFromUrl || null
  if (!pacsId) {
    return {
      serverUrl: '',
      username: '',
      password: '',
      pacsConfig: null,
      pacsId: null,
    }
  }
  const pacsConfig = getPacsConfig(pacsId)
  const serverUrl = buildOrthancUrl(pacsConfig)
  console.warn('[UDV-PACS-v3] resolvePacsState', {
    pacsId,
    serverUrl,
    username: pacsConfig.USUARIO,
    aetitle: pacsConfig.AETITLE,
    ip: pacsConfig.IP,
  })
  return {
    serverUrl,
    username: pacsConfig.USUARIO,
    password: pacsConfig.PASS,
    pacsConfig,
    pacsId,
  }
}

function todayIsoDate() {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function isoToDicomDate(iso) {
  return String(iso || '')
    .trim()
    .replace(/-/g, '')
    .slice(0, 8)
}

class OrthancConnection extends Component {
  constructor(props) {
    super(props)
    console.log('OrthancConnection - constructor called with props:', props)
    const pacsState = resolvePacsState(props.pacsIdFromUrl)
    const today = todayIsoDate()
    this.state = {
      ...pacsState,
      studies: [],
      selectedStudy: null,
      error: null,
      loading: false,
      search: '',
      studyLoadStarted: false,
      filterDateFrom: today,
      filterDateTo: today,
      filterPatientId: '',
      filterPatientName: '',
    }
  }

  applyWadoAuth = (username, password) => {
    // Unified: no Authorization on R2/S3 presigned; Basic only for Orthanc WADO
    configureWadoLoaderAuth({ username, password })
  }

  componentDidMount() {
    console.log('OrthancConnection - componentDidMount [UDV-PACS-v3]')
    
    // Reforzar la inicialización del explorer
    const initialExplorerState = {
      folder: null,
      patient: {
        list: new Map(),
        keys: []
      }
    }
    this.props.explorer(initialExplorerState)
    
    // Configurar el loader WADO (workers: init global en DicomViewer)
    cornerstoneWADOImageLoader.external.cornerstone = cornerstone
    cornerstoneWADOImageLoader.external.dicomParser = dicomParser
    if (!window.__cornerstoneWebWorkerManagerInitialized) {
      try {
        cornerstoneWADOImageLoader.webWorkerManager.initialize({
          maxWebWorkers: navigator.hardwareConcurrency || 1,
          startWebWorkersOnDemand: true,
          taskConfiguration: {
            decodeTask: {
              initializeCodecsOnStartup: true,
              usePDFJS: false,
              strict: false
            }
          }
        })
        window.__cornerstoneWebWorkerManagerInitialized = true
      } catch (e) {
        // Ya inicializado
      }
    }
    
    this.applyWadoAuth(this.state.username, this.state.password)

    // Solo auto-cargar Orthanc si el diálogo está abierto (loadStudyFromUrl).
    // Con ?manifestUrl=&studyId= el studyId es StudyInstanceUID (no Orthanc id);
    // cargar aquí abortaba el fetch R2 vía beginStudyLoad.
    if (this.props.open && this.props.studyIdFromUrl) {
      this.setState({ studyLoadStarted: true }, () => {
        this.handleConnectAndLoadStudy(this.props.studyIdFromUrl)
      })
    }
  }

  componentDidUpdate(prevProps) {
    // Al abrir el diálogo o cambiar ?pacs=, recargar config desde pacsConfig.js
    const opened = this.props.open && !prevProps.open
    const pacsChanged = this.props.pacsIdFromUrl !== prevProps.pacsIdFromUrl
    if (opened || pacsChanged) {
      let pacsId = this.props.pacsIdFromUrl
      if (!pacsId && this.props.allowPacsPick) {
        pacsId = this.state.pacsId || 'DEMOPORTAL'
      }
      const pacsState = resolvePacsState(pacsId)
      this.setState(
        {
          ...pacsState,
          filterDateFrom: this.state.filterDateFrom || todayIsoDate(),
          filterDateTo: this.state.filterDateTo || todayIsoDate(),
        },
        () => {
          if (pacsState.username) {
            this.applyWadoAuth(pacsState.username, pacsState.password)
          }
        }
      )
    }

    // Cargar estudio cuando open pasa a true con studyId (montaje temprano sin props listas)
    const studyChanged = this.props.studyIdFromUrl !== prevProps.studyIdFromUrl
    const reloadRequested =
      this.props.reloadToken !== prevProps.reloadToken &&
      this.props.reloadToken > 0
    if (
      this.props.open &&
      this.props.studyIdFromUrl &&
      (opened || studyChanged || reloadRequested)
    ) {
      this.setState({ studyLoadStarted: true }, () => {
        this.handleConnectAndLoadStudy(this.props.studyIdFromUrl)
      })
    }
  }

  handleInputChange = (event) => {
    const { name, value } = event.target
    this.setState({ [name]: value })
  }

  handleConnect = async () => {
    console.log('Attempting to connect to Orthanc server...')
    if (!this.state.serverUrl || !this.state.pacsId) {
      this.setState({
        error: 'Seleccioná un PACS antes de conectar.',
        loading: false,
      })
      return
    }
    this.setState({ loading: true, error: null })

    try {
      const response = await fetch(`${this.state.serverUrl}/system`, {
        method: 'GET',
        headers: {
          'Authorization': 'Basic ' + btoa(`${this.state.username}:${this.state.password}`)
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      console.log('Orthanc server response:', data)

      await this.loadStudies()
    } catch (error) {
      console.error('Error connecting to Orthanc:', error)
      this.setState({ 
        error: `Error connecting to Orthanc server: ${error.message}`,
        loading: false 
      })
    }
  }

  handlePacsPick = (event) => {
    const pacsId = event.target.value
    const pacsState = resolvePacsState(pacsId || null)
    this.setState(
      {
        ...pacsState,
        studies: [],
        error: null,
      },
      () => {
        if (pacsState.username) {
          this.applyWadoAuth(pacsState.username, pacsState.password)
        }
      }
    )
  }

  loadStudies = async () => {
    console.log('Loading studies from Orthanc (tools/find)...')
    try {
      const fromDicom = isoToDicomDate(this.state.filterDateFrom)
      const toDicom = isoToDicomDate(this.state.filterDateTo) || fromDicom
      const query = {}
      if (fromDicom) {
        query.StudyDate =
          fromDicom === toDicom ? fromDicom : `${fromDicom}-${toDicom}`
      }
      const pid = String(this.state.filterPatientId || '').trim()
      if (pid) query.PatientID = pid
      const pname = String(this.state.filterPatientName || '').trim()
      if (pname) {
        query.PatientName = pname.indexOf('*') >= 0 ? pname : `*${pname}*`
      }

      const response = await fetch(`${this.state.serverUrl}/tools/find`, {
        method: 'POST',
        headers: {
          Authorization: 'Basic ' + btoa(`${this.state.username}:${this.state.password}`),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          Level: 'Study',
          Query: query,
          Expand: true,
          Limit: 200,
        }),
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const raw = await response.json()
      const list = Array.isArray(raw) ? raw : []
      const studies = list
        .map((studyData) => {
          if (!studyData || !studyData.ID) return null
          const studyDate = studyData.MainDicomTags?.StudyDate || ''
          const formattedDate = studyDate
            ? `${studyDate.substring(0, 4)}-${studyDate.substring(4, 6)}-${studyDate.substring(6, 8)}`
            : ''
          return {
            ID: studyData.ID,
            key: studyData.ID,
            StudyDescription: studyData.MainDicomTags?.StudyDescription || '',
            StudyDate: formattedDate,
            InstitutionName: studyData.MainDicomTags?.InstitutionName || '',
            PatientName:
              studyData.PatientMainDicomTags?.PatientName || 'Unknown Patient',
            PatientID: studyData.PatientMainDicomTags?.PatientID || 'N/A',
          }
        })
        .filter(Boolean)

      console.log('Studies found:', studies.length)
      this.setState({ studies, loading: false, error: null })
    } catch (error) {
      console.error('Error loading studies:', error)
      this.setState({
        error: `Error loading studies: ${error.message}`,
        loading: false,
        studies: [],
      })
    }
  }

  /** Invalida la carga Orthanc en curso (switch de estudio o Cancelar). */
  cancelActiveLoad = () => {
    this._studyLoadSeq = (this._studyLoadSeq || 0) + 1
    this._activeLoadToken = null
  }

  loadStudy = async (studyId) => {
    this._studyLoadSeq = (this._studyLoadSeq || 0) + 1
    const loadSeq = this._studyLoadSeq
    let loadToken = null
    try {
      if (typeof this.props.onStudyLoadBegin === 'function') {
        loadToken = this.props.onStudyLoadBegin(studyId) || null
      }
      this._activeLoadToken = loadToken
      if (typeof this.props.onPrepareNewStudy === 'function') {
        this.props.onPrepareNewStudy(studyId)
      }
      const authHeader = {
        Authorization: 'Basic ' + btoa(`${this.state.username}:${this.state.password}`),
      }
      const studyUrl = `${this.state.serverUrl}/studies/${studyId}`
      console.warn('[UDV-PACS] loadStudy →', studyUrl)

      if (this.props.onExplorerVisible) {
        this.props.onExplorerVisible(false)
      }

      const isStale = () =>
        loadSeq !== this._studyLoadSeq ||
        !!(loadToken && loadToken.signal && loadToken.signal.aborted)

      // Hybrid R2: intent presigner GET /studies/{id}/r2-manifest → píxeles directos
      const pacsConfig = this.state.pacsConfig || getPacsConfig(this.state.pacsId)
      if (isR2DirectEnabled(pacsConfig)) {
        const loadedR2 = await this.tryLoadStudyFromR2Manifest(
          studyId,
          authHeader,
          loadSeq,
          loadToken
        )
        if (isStale()) return
        if (loadedR2) {
          return
        }
        console.warn(
          '[UDV-PACS] R2 manifest no disponible; fallback WADO Orthanc:',
          studyId
        )
      }

      if (isStale()) return
      await this.loadStudyViaWado(studyId, authHeader, studyUrl, loadSeq, loadToken)
    } catch (error) {
      if (error && error.name === 'AbortError') return
      console.error('OrthancConnection - Error loading study:', error)
      this.setState({
        error: `Error loading study: ${error.message}`,
      })
    } finally {
      if (this._activeLoadToken === loadToken) {
        this._activeLoadToken = null
      }
    }
  }

  /**
   * Handshake presigner → loadStudyFromManifestObject (memoria, no JSON en R2).
   * Key R2 por instancia: ${instanceId}.dcm (flat).
   * @returns {Promise<boolean>} true si cargó; false → usar WADO
   */
  tryLoadStudyFromR2Manifest = async (studyId, authHeader, loadSeq, loadToken) => {
    const pacsConfig = this.state.pacsConfig || getPacsConfig(this.state.pacsId)
    const manifestUrl = buildR2ManifestUrl(pacsConfig, studyId)
    console.warn('[UDV-PACS] try R2 manifest →', manifestUrl)

    let res
    try {
      res = await fetch(manifestUrl, { headers: authHeader })
    } catch (e) {
      console.warn('[UDV-PACS] r2-manifest network error', e)
      return false
    }

    if (res.status === 404 || res.status === 501) {
      return false
    }
    if (!res.ok) {
      console.warn('[UDV-PACS] r2-manifest HTTP', res.status)
      return false
    }

    let manifest
    try {
      manifest = await res.json()
    } catch (e) {
      console.warn('[UDV-PACS] r2-manifest JSON inválido', e)
      return false
    }

    if (loadSeq != null && loadSeq !== this._studyLoadSeq) return false

    this.props.onClose()
    if (this.props.onR2LoadStart) {
      this.props.onR2LoadStart()
    }

    try {
      let assetBaseUrl = null
      try {
        if (manifestUrl) {
          const u = new URL(manifestUrl)
          assetBaseUrl = `${u.origin}/`
        }
      } catch (e) {
        /* ignore */
      }
      const handlersMeta = {
        manifestUrl,
        assetBaseUrl,
        orthancCredentials: {
          username: this.state.username,
          password: this.state.password,
        },
      }
      if (this.props.onManifestLoaded) {
        this.props.onManifestLoaded(manifest, handlersMeta)
      }
      let mode = 'DIAGNOSTIC'
      if (typeof this.props.resolveRenderModeForManifest === 'function') {
        mode = this.props.resolveRenderModeForManifest(manifest, assetBaseUrl)
      } else if (typeof this.props.getRenderMode === 'function') {
        mode = this.props.getRenderMode() || 'DIAGNOSTIC'
      }
      await loadStudyFromManifestObject(manifest, {
        setFilesStore: (files) => {
          if (loadSeq != null && loadSeq !== this._studyLoadSeq) return
          this.props.filesStore(files)
        },
        onExplorerUpdate: (data) => {
          if (loadSeq != null && loadSeq !== this._studyLoadSeq) return
          this.props.onExplorerUpdate(data)
        },
        isOpenStore: this.props.isOpenStore,
        activeDcmIndex: this.props.activeDcmIndex,
        concurrency: 0,
        orthancCredentials: handlersMeta.orthancCredentials,
        manifestUrl,
        assetBaseUrl,
        renderMode: mode,
        getActiveSeriesIdx: () =>
          typeof this.props.explorerActiveSeriesIndex === 'number'
            ? this.props.explorerActiveSeriesIndex
            : 0,
        getPinnedSeriesIdx:
          typeof this.props.getPinnedSeriesIdx === 'function'
            ? this.props.getPinnedSeriesIdx
            : () => [],
        getRenderMode: () => mode,
        signal: loadToken && loadToken.signal,
        isCancelled: () =>
          (loadSeq != null && loadSeq !== this._studyLoadSeq) ||
          !!(loadToken && loadToken.signal && loadToken.signal.aborted),
        onDownloadProgress: (stats) => {
          if (loadSeq != null && loadSeq !== this._studyLoadSeq) return
          if (loadToken && loadToken.signal && loadToken.signal.aborted) return
          if (this.props.onR2DownloadProgress) {
            this.props.onR2DownloadProgress(stats)
          }
        },
      })
      console.log('OrthancConnection - Study loaded via R2 manifest:', studyId)
      return true
    } catch (e) {
      if (e && e.name === 'AbortError') return false
      console.error('OrthancConnection - R2 manifest load failed:', e)
      return false
    } finally {
      if (
        (loadSeq == null || loadSeq === this._studyLoadSeq) &&
        !(loadToken && loadToken.signal && loadToken.signal.aborted) &&
        this.props.onR2LoadEnd
      ) {
        this.props.onR2LoadEnd()
      }
    }
  }

  /** Flujo clásico: metadata Orthanc + WADO (egress VPS), con caché IndexedDB por SOP. */
  loadStudyViaWado = async (studyId, authHeader, studyUrl, loadSeq, loadToken) => {
    const isStale = () =>
      (loadSeq != null && loadSeq !== this._studyLoadSeq) ||
      !!(loadToken && loadToken.signal && loadToken.signal.aborted)

    const studyResponse = await fetch(studyUrl, {
      headers: authHeader,
      signal: loadToken && loadToken.signal,
    })
    if (!studyResponse.ok) {
      const body = await studyResponse.text()
      throw new Error(`HTTP ${studyResponse.status} en ${studyUrl} — ${body.slice(0, 200)}`)
    }
    const studyDetails = await studyResponse.json()
    console.log('Study details:', studyDetails)

    const studyInstanceUID =
      (studyDetails.MainDicomTags && studyDetails.MainDicomTags.StudyInstanceUID) || ''

    const seriesResponse = await fetch(
      `${this.state.serverUrl}/studies/${studyId}/series`,
      { headers: authHeader, signal: loadToken && loadToken.signal }
    )
    const seriesList = await seriesResponse.json()
    console.log('Series list:', seriesList)

    if (isStale()) return

    this.props.onClose()

    // Reutilizar panel de stats de App (mismo contrato que R2)
    if (this.props.onR2LoadStart) {
      this.props.onR2LoadStart()
    }

    let items = []
    let fromCacheHits = 0
    let offlineAtStart = 0
    const firstKeyOfSeries = new Set()

    const lightenItems = (list) => {
      const firstSeen = new Set()
      return list.map((it) => {
        const key = `${(it.series && it.series.seriesNumber) || ''}|${(it.series && it.series.seriesDescription) || ''}|${(it.study && it.study.studyId) || ''}`
        if (!firstSeen.has(key)) {
          firstSeen.add(key)
          return it
        }
        if (!it.image) return it
        return { ...it, image: null }
      })
    }

    try {
      const completeSet = await listCompleteSopsForStudy(studyInstanceUID)
      offlineAtStart = completeSet.size
    } catch (e) {
      /* ignore */
    }

    const updateExplorer = () => {
      if (this.props.onExplorerUpdate && items.length > 0) {
        const patientList = groupBy(items, (a) => a.patient.patientName)
        const patientKeys = [...patientList.keys()]
        const explorerData = {
          folder: null,
          patient: {
            list: patientList,
            keys: patientKeys,
          },
        }
        this.props.onExplorerUpdate(explorerData)
        this.props.isOpenStore({ index: this.props.activeDcmIndex, value: true })
        if (items.length > 0 && this.props.onExplorerVisible) {
          this.props.onExplorerVisible(true)
        }
      }
    }

    // Contar instancias primero para target offline y throttle de Redux
    let totalInstances = 0
    const seriesInstances = []
    for (const series of seriesList) {
      try {
        const instancesResponse = await fetch(
          `${this.state.serverUrl}/series/${series.ID}/instances`,
          { headers: authHeader }
        )
        const instances = await instancesResponse.json()
        totalInstances += instances.length
        seriesInstances.push({ series, instances })
      } catch (e) {
        console.error('Error listing instances for series:', series.ID, e)
        seriesInstances.push({ series, instances: [] })
      }
    }
    this._wadoTargetCount = totalInstances
    this._wadoFullyCachedAtStart =
      totalInstances > 0 && offlineAtStart >= totalInstances

    const tasks = []
    seriesInstances.forEach((entry, seriesIdx) => {
      const { series, instances } = entry
      const totalImagesInSeries = instances.length
      instances.forEach((instance) => {
        tasks.push({ series, seriesIdx, instance, totalImagesInSeries })
      })
    })

    const numSeries = seriesInstances.length
    const seriesSlots = buildSeriesSlots(tasks)
    const seriesInfo = seriesInstances.map((entry, idx) => {
      const tags = (entry.series && entry.series.MainDicomTags) || {}
      return {
        seriesNumber: parseInt(tags.SeriesNumber || '0', 10) || idx + 1,
        description: String(tags.SeriesDescription || '').trim(),
        totalTasks: (seriesSlots.get(idx) || []).length,
      }
    })

    const parallel = Math.max(1, getManifestDownloadConcurrency(tasks.length))
    const scheduler = createSeriesPriorityScheduler({
      numSeries,
      seriesSlots,
      seriesInfo,
      parallel,
      getActiveSeriesIdx: () =>
        typeof this.props.explorerActiveSeriesIndex === 'number'
          ? this.props.explorerActiveSeriesIndex
          : 0,
      getPinnedSeriesIdx:
        typeof this.props.getPinnedSeriesIdx === 'function'
          ? this.props.getPinnedSeriesIdx
          : () => [],
    })

    const progressTracker = createDownloadProgressTracker({
      totalTasks: tasks.length,
      seriesInfo,
      throttleMs: 220,
      onProgress: (stats) => {
        if (isStale()) return
        if (this.props.onR2DownloadProgress) {
          this.props.onR2DownloadProgress(stats)
        }
      },
      loadSource: () => {
        const { loadedOk } = progressTracker.totals
        const studyFullyCachedAtStart = !!this._wadoFullyCachedAtStart
        if (
          studyFullyCachedAtStart ||
          (loadedOk > 0 && fromCacheHits >= loadedOk * 0.8)
        ) {
          return 'local'
        }
        return 'r2'
      },
      extraStats: () => {
        const { loadedOk, totalBytes } = progressTracker.totals
        let wq = { queueDepth: 0, putsCompleted: 0, writesFailed: 0 }
        try {
          wq = getWriteQueueStats()
        } catch (e) {
          /* ignore */
        }
        const target = this._wadoTargetCount || Math.max(tasks.length, 1)
        const studyFullyCachedAtStart = !!this._wadoFullyCachedAtStart
        const completeCount = Math.min(
          target,
          offlineAtStart + Math.max(0, loadedOk - fromCacheHits)
        )
        return {
          studyInstanceUID,
          offlineCache: {
            completeCount,
            fromCacheHits,
            cachedAtStart: offlineAtStart,
            studyFullyCachedAtStart,
            targetCount: target,
            studyOffline: studyFullyCachedAtStart,
            bytesCached: totalBytes,
            studyInstanceUID,
            writeQueueDepth: wq.queueDepth,
            writesPending: wq.queueDepth,
            writesCompleted: wq.putsCompleted,
            writesFailed: wq.writesFailed,
          },
        }
      },
    })

    progressTracker.emit(true)

    const publishStore = throttle(
      () => {
        if (isStale()) return
        if (items.length === 0) return
        const sorted = items.slice().sort((a, b) => a.instanceNumber - b.instanceNumber)
        this.props.filesStore(lightenItems(sorted))
        updateExplorer()
      },
      totalInstances > 400 ? 800 : 250,
      { leading: true, trailing: true }
    )

    const orderedItems = new Array(tasks.length)

    const loadOne = async (task, slot) => {
      const { series, instance, totalImagesInSeries, seriesIdx } = task
      const seriesKey = `${(series.MainDicomTags && series.MainDicomTags.SeriesInstanceUID) || series.ID}`
      const sop =
        (instance.MainDicomTags && instance.MainDicomTags.SOPInstanceUID) || null
      const wadoUrl = `${this.state.serverUrl}/wado?requestType=WADO&studyUID=${studyDetails.MainDicomTags.StudyInstanceUID}&seriesUID=${series.MainDicomTags.SeriesInstanceUID}&objectUID=${sop || ''}&contentType=application/dicom`
      let imageId = `wadouri:${wadoUrl}`
      let ok = false
      let bytes = 0
      let fromCache = false

      try {
        let image
        let resolvedSop = sop
        try {
          const loaded = await loadDicomInstanceWithCache({
            absoluteUrl: wadoUrl,
            inst: { sopInstanceUID: sop },
            studyInstanceUID,
            sopInstanceUID: sop,
            fetchHeaders: authHeader,
          })
          if (isStale()) return { ok: false, bytes: 0, aborted: true }
          image = loaded.image
          imageId = loaded.imageId
          fromCache = !!loaded.fromCache
          if (loaded.sopInstanceUID) resolvedSop = loaded.sopInstanceUID
        } catch (cacheErr) {
          if (isStale()) return { ok: false, bytes: 0, aborted: true }
          console.warn(
            'OrthancConnection: cache path failed, fallback wadouri',
            cacheErr
          )
          const direct = await loadDicomInstanceDirectUrl(wadoUrl)
          if (isStale()) return { ok: false, bytes: 0, aborted: true }
          image = direct.image
          imageId = direct.imageId
          fromCache = false
        }

        if (fromCache) fromCacheHits += 1
        ok = !!imageId
        bytes = estimateLoadedBytes(image)

        const keepPixels = !firstKeyOfSeries.has(seriesKey)
        if (keepPixels) firstKeyOfSeries.add(seriesKey)

        const seriesInstanceUID =
          (series.MainDicomTags && series.MainDicomTags.SeriesInstanceUID) || null
        let resolvedSeriesUid = seriesInstanceUID
        if (!resolvedSeriesUid && image && image.data) {
          try {
            resolvedSeriesUid = image.data.string('x0020000e') || null
          } catch (e) {
            /* ignore */
          }
        }

        const item = {
          imageId,
          instanceNumber: parseInt(instance.MainDicomTags.InstanceNumber || '0'),
          name: `${instance.MainDicomTags.InstanceNumber || '0'}.dcm`,
          image: keepPixels ? image : null,
          sopInstanceUID: resolvedSop || null,
          seriesInstanceUID: resolvedSeriesUid || null,
          fromCache,
          patient: {
            patientName: studyDetails.PatientMainDicomTags.PatientName || 'Unknown Patient',
            patientId: studyDetails.PatientMainDicomTags.PatientID || 'N/A',
          },
          study: {
            studyId: studyDetails.MainDicomTags.StudyInstanceUID,
            studyDate: studyDetails.MainDicomTags.StudyDate,
            studyTime: studyDetails.MainDicomTags.StudyTime,
            studyDateTime: `${studyDetails.MainDicomTags.StudyDate || ''}.${studyDetails.MainDicomTags.StudyTime || ''}`,
            studyDescription: studyDetails.MainDicomTags.StudyDescription || '',
          },
          series: {
            seriesDate: series.MainDicomTags.SeriesDate,
            seriesTime: series.MainDicomTags.SeriesTime,
            seriesDescription: series.MainDicomTags.SeriesDescription || '',
            seriesNumber: parseInt(series.MainDicomTags.SeriesNumber || '0'),
            seriesInstanceUID: resolvedSeriesUid || null,
            echoNumber: parseInt(series.MainDicomTags.EchoNumber || '0'),
            totalImages: totalImagesInSeries,
            modality: (series.MainDicomTags && series.MainDicomTags.Modality) || '',
          },
        }
        stampIsScoutOnFileItem(item)
        orderedItems[slot] = item
        items.push(item)
        return { ok, bytes, fromCache }
      } catch (error) {
        console.warn('Error loading image for instance:', instance.ID, {
          error: error.message || error,
          wadoUrl,
          instanceId: instance.ID,
        })
        const item = {
          imageId,
          instanceNumber: parseInt(instance.MainDicomTags.InstanceNumber || '0'),
          name: `${instance.MainDicomTags.InstanceNumber || '0'}.dcm`,
          image: null,
          sopInstanceUID: sop || null,
          error: error.message || 'Failed to load image',
          patient: {
            patientName: studyDetails.PatientMainDicomTags.PatientName || 'Unknown Patient',
            patientId: studyDetails.PatientMainDicomTags.PatientID || 'N/A',
          },
          study: {
            studyId: studyDetails.MainDicomTags.StudyInstanceUID,
            studyDate: studyDetails.MainDicomTags.StudyDate,
            studyTime: studyDetails.MainDicomTags.StudyTime,
            studyDateTime: `${studyDetails.MainDicomTags.StudyDate || ''}.${studyDetails.MainDicomTags.StudyTime || ''}`,
            studyDescription: studyDetails.MainDicomTags.StudyDescription || '',
          },
          series: {
            seriesDate: series.MainDicomTags.SeriesDate,
            seriesTime: series.MainDicomTags.SeriesTime,
            seriesDescription: series.MainDicomTags.SeriesDescription || '',
            seriesNumber: parseInt(series.MainDicomTags.SeriesNumber || '0'),
            echoNumber: parseInt(series.MainDicomTags.EchoNumber || '0'),
            totalImages: totalImagesInSeries,
            modality: (series.MainDicomTags && series.MainDicomTags.Modality) || '',
          },
        }
        stampIsScoutOnFileItem(item)
        orderedItems[slot] = item
        items.push(item)
        return { ok: false, bytes: 0, fromCache: false }
      }
    }

    async function worker() {
      while (!scheduler.isDone() && scheduler.completed.size < tasks.length) {
        if (isStale()) return
        const slot = scheduler.pickNextSlot()
        if (slot == null) {
          if (scheduler.completed.size >= tasks.length) return
          await new Promise((r) => setTimeout(r, 2))
          continue
        }
        const task = tasks[slot]
        try {
          const result = await loadOne(task, slot)
          if (result && result.aborted) return
          progressTracker.recordTaskDone({
            seriesIdx: task.seriesIdx,
            ok: !!(result && result.ok),
            bytes: (result && result.bytes) || 0,
          })
          publishStore()
          const { totalDone } = progressTracker.totals
          if (totalDone % 5 === 0 || (result && result.fromCache)) {
            progressTracker.emit(false)
          }
        } catch (e) {
          progressTracker.recordTaskDone({
            seriesIdx: task.seriesIdx,
            ok: false,
            bytes: 0,
          })
        } finally {
          scheduler.finishSlot(slot)
        }
      }
    }

    try {
      if (tasks.length > 0) {
        await Promise.all(Array.from({ length: parallel }, () => worker()))
      }

      if (isStale()) return

      if (items.length === 0) {
        throw new Error('No images could be loaded from the study')
      }

      if (publishStore.flush) publishStore.flush()
      else {
        const sorted = items.slice().sort((a, b) => a.instanceNumber - b.instanceNumber)
        this.props.filesStore(lightenItems(sorted))
        updateExplorer()
      }

      progressTracker.emit(true)

      console.log('OrthancConnection - Study loaded successfully (WADO+cache):', {
        studyId,
        patientName: studyDetails.PatientMainDicomTags.PatientName,
        seriesCount: seriesList.length,
        instanceCount: items.length,
        fromCacheHits,
      })
    } finally {
      if (publishStore.cancel) publishStore.cancel()
      if (!isStale() && this.props.onR2LoadEnd) {
        this.props.onR2LoadEnd()
      }
    }
  }

  handleSearchChange = (event) => {
    this.setState({ search: event.target.value })
  }

  // Nuevo método para conectar y cargar estudio en un solo paso
  handleConnectAndLoadStudy = async (studyId) => {
    console.log('OrthancConnection - handleConnectAndLoadStudy called with studyId:', studyId)
    console.log('OrthancConnection - serverUrl:', this.state.serverUrl)
    console.log('OrthancConnection - username:', this.state.username)
    console.log('OrthancConnection - pacsId:', this.state.pacsId)
    
    this.setState({ loading: true, error: null })

    try {
        // Conectar al servidor
        console.log('OrthancConnection - Intentando conectar a:', `${this.state.serverUrl}/system`)
        const response = await fetch(`${this.state.serverUrl}/system`, {
            method: 'GET',
            headers: {
                'Authorization': 'Basic ' + btoa(`${this.state.username}:${this.state.password}`)
            }
        })

        console.log('OrthancConnection - Respuesta del servidor:', response.status, response.statusText)

        if (!response.ok) {
            const errorText = await response.text()
            console.error('OrthancConnection - Error del servidor:', errorText)
            throw new Error(`HTTP error! status: ${response.status} - ${errorText}`)
        }

        const systemData = await response.json()
        console.log('OrthancConnection - Sistema conectado exitosamente:', systemData)

        // Si la conexión es exitosa, cargar el estudio directamente
        await this.loadStudy(studyId)
    } catch (error) {
        console.error('OrthancConnection - Error in handleConnectAndLoadStudy:', error)
        console.error('OrthancConnection - Detalles del error:', {
            message: error.message,
            stack: error.stack,
            serverUrl: this.state.serverUrl,
            username: this.state.username
        })
        this.setState({ 
            error: `Error conectando al servidor: ${error.message}. Verifique la configuración del PACS.`,
            loading: false 
        })
    }
  }

  render() {
    const {
      serverUrl,
      username,
      password,
      studies,
      error,
      loading,
      pacsId,
      pacsConfig,
      filterDateFrom,
      filterDateTo,
      filterPatientId,
      filterPatientName,
    } = this.state
    const { studyIdFromUrl, allowPacsPick } = this.props
    const pacsList = getAvailablePacs()

    // Si hay un StudyId en la URL, mostrar un mensaje de carga en lugar del diálogo completo
    if (studyIdFromUrl) {
        return (
            <Dialog open={this.props.open} onClose={this.props.onClose}>
                <DialogTitle>
                  Cargando Estudio
                  {pacsId && (
                    <Typography variant="caption" style={{ display: 'block', marginTop: 4 }}>
                      PACS: {pacsId} ({pacsConfig?.AETITLE || 'N/A'})
                    </Typography>
                  )}
                </DialogTitle>
                <DialogContent>
                    {error ? (
                        <Typography color="error" style={{ marginTop: 16 }}>
                            {error}
                        </Typography>
                    ) : (
                        <Typography style={{ marginTop: 16 }}>
                            Cargando estudio {studyIdFromUrl}...
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={this.props.onClose} color="primary">
                        Cancelar
                    </Button>
                </DialogActions>
            </Dialog>
        )
    }

    // Si no hay StudyId en la URL, mostrar el diálogo normal
    return (
      <Dialog open={this.props.open} onClose={this.props.onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Conectar a Servidor Orthanc
          <Typography variant="caption" style={{ display: 'block', marginTop: 4, opacity: 0.85 }}>
            {pacsId
              ? `PACS: ${pacsId} · ${pacsConfig?.AETITLE || 'N/A'} · ${serverUrl}`
              : 'Seleccioná un PACS'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {allowPacsPick ? (
            <TextField
              select
              margin="dense"
              label="PACS"
              fullWidth
              value={pacsId || ''}
              onChange={this.handlePacsPick}
              SelectProps={{ native: true }}
              helperText="Developer: elegir destino"
            >
              <option value="">—</option>
              {pacsList.map((id) => (
                <option key={id} value={id}>
                  {id}
                </option>
              ))}
            </TextField>
          ) : null}
          {allowPacsPick ? (
            <>
              <TextField
                autoFocus={!allowPacsPick}
                margin="dense"
                name="serverUrl"
                label="Server URL"
                type="text"
                fullWidth
                value={serverUrl}
                onChange={this.handleInputChange}
              />
              <TextField
                margin="dense"
                name="username"
                label="Username"
                type="text"
                fullWidth
                value={username}
                onChange={this.handleInputChange}
              />
              <TextField
                margin="dense"
                name="password"
                label="Password"
                type="password"
                fullWidth
                value={password}
                onChange={this.handleInputChange}
              />
            </>
          ) : null}
          <Typography
            variant="caption"
            style={{ display: 'block', marginTop: 12, marginBottom: 4, opacity: 0.8 }}
          >
            Filtros de búsqueda (por defecto: estudios de hoy)
          </Typography>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <TextField
              margin="dense"
              name="filterDateFrom"
              label="Desde"
              type="date"
              value={filterDateFrom}
              onChange={this.handleInputChange}
              InputLabelProps={{ shrink: true }}
              style={{ flex: '1 1 140px' }}
            />
            <TextField
              margin="dense"
              name="filterDateTo"
              label="Hasta"
              type="date"
              value={filterDateTo}
              onChange={this.handleInputChange}
              InputLabelProps={{ shrink: true }}
              style={{ flex: '1 1 140px' }}
            />
          </div>
          <TextField
            margin="dense"
            name="filterPatientId"
            label="ID paciente"
            type="text"
            fullWidth
            value={filterPatientId}
            onChange={this.handleInputChange}
            autoComplete="off"
          />
          <TextField
            margin="dense"
            name="filterPatientName"
            label="Nombre (parcial)"
            type="text"
            fullWidth
            value={filterPatientName}
            onChange={this.handleInputChange}
            style={{ marginBottom: 8 }}
            autoComplete="off"
            helperText="Se busca con comodines (*nombre*)"
          />
          {error && (
            <Typography color="error" style={{ marginTop: 16 }}>
              {error}
            </Typography>
          )}
          {loading && (
            <Typography style={{ marginTop: 16 }}>
              Buscando estudios...
            </Typography>
          )}
          
          {studies && studies.length > 0 && (
            <List dense>
              {studies.map((study) => (
                <ListItem 
                  button 
                  key={study.key}
                  onClick={() => this.loadStudy(study.ID)}
                >
                  <ListItemText
                    primary={`${study.PatientName} (${study.PatientID})`}
                    secondary={
                      <>
                        <Typography component="span" variant="body2" color="textPrimary">
                          {study.StudyDescription}
                        </Typography>
                        {` — ${study.StudyDate} — ${study.InstitutionName}`}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
          {!loading && studies && studies.length === 0 && pacsId ? (
            <Typography variant="body2" style={{ marginTop: 12, opacity: 0.7 }}>
              Sin resultados para los filtros actuales.
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={this.props.onClose} color="primary">
            Cancelar
          </Button>
          <Button
            onClick={this.handleConnect}
            color="primary"
            disabled={!pacsId || loading}
          >
            Buscar
          </Button>
        </DialogActions>
      </Dialog>
    )
  }
}

// Define las propiedades por defecto
OrthancConnection.defaultProps = {
  onExplorerVisible: null,
  onLoadImage: null,
  onExplorerUpdate: null,
  onR2LoadStart: null,
  onR2DownloadProgress: null,
  onR2LoadEnd: null,
  allowPacsPick: false,
}

// Actualizar el mapStateToProps para incluir activeDcmIndex y serie activa (cola R2)
const mapStateToProps = (state) => ({
  activeDcmIndex: state.activeDcmIndex,
  explorerActiveSeriesIndex: state.explorerActiveSeriesIndex,
})

// Actualizar el mapDispatchToProps para usar dcmIsOpen
const mapDispatchToProps = (dispatch) => ({
  filesStore: (files) => dispatch(filesStore(files)),
  explorer: (data) => dispatch(explorer(data)),
  isOpenStore: (value) => dispatch(dcmIsOpen(value))
})

export default connect(mapStateToProps, mapDispatchToProps, null, {
  forwardRef: true,
})(OrthancConnection) 
